/**
 * Test copies (DECISIONS 23 Oct, built 26 Oct 2026): "Test this site" makes a
 * new url doc that is a copy of the site's live version, with its own slug,
 * test link and userTesting config. Design: research/app-v3/TEST-COPY.md.
 *
 * The copy's version doc has the same manifest as the site's live version, so
 * it points at the same stored files (blobs/{uid}/{hash}): nothing is uploaded
 * again, and nothing ever deletes blobs. "Upload to this test" adds a version
 * to the copy only (deploySite on the copy's doc), never to the site.
 *
 * A copy is marked by `sourceSiteId`. Older tests that live on a site's own
 * doc (userTesting on the site) carry no such field and keep working as they
 * are.
 *
 * Separate from sitesUtils.copyActiveVersion on purpose: that helper is also
 * used by Figmafolio (UserDashboard.js) and writes in two steps.
 */
import firebase from '../../firebase';
import { generateReadableSlug, isValidReadableSlug } from '../AeroSites/sitesUtils';

export const UT_DEFAULTS = {
  audioEnabled: true,
  videoEnabled: true,
  askDifficulty: false,
  welcomeMessage: "Thanks for helping test this design. Your feedback shapes what happens next.",
  thankYouMessage: 'Thank you for your feedback!',
};

export function taskDocs(tasks) {
  return (tasks || [])
    .map((t) => (t || '').trim())
    .filter(Boolean)
    .map((title, i) => ({ id: `task-${i + 1}`, title, order: i + 1 }));
}

/** A url doc (or row) that is a test copy of a site. */
export function isTestCopy(d) {
  return !!(d && typeof d.sourceSiteId === 'string' && d.sourceSiteId);
}

/**
 * Slug candidates for the nth test of a site: "pocket-test", "pocket-test-2".
 * The base is trimmed so the result stays a valid readable slug (30 chars).
 */
export function testSlugCandidates(siteSlug, n) {
  const clean = String(siteSlug || '').toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const out = [];
  for (let i = Math.max(1, n); i < Math.max(1, n) + 4; i++) {
    const suffix = i === 1 ? '-test' : `-test-${i}`;
    const base = clean.slice(0, 30 - suffix.length).replace(/-$/, '');
    if (!base) continue;
    const s = `${base}${suffix}`;
    if (isValidReadableSlug(s)) out.push(s);
  }
  return out;
}

/** "Pocket" for the first test of a site, then "Pocket test 2". */
export function testCopyTitle(siteTitle, n, name) {
  const given = (name || '').trim();
  if (given) return given;
  const base = (siteTitle || '').trim() || 'Untitled site';
  return n > 1 ? `${base} test ${n}` : base;
}

/**
 * The two docs a copy is made of. Pure (no Firestore), so it can be tested.
 * `stamp` is the value used for createdAt / updatedAt (a server timestamp in
 * the app).
 */
export function buildTestCopyDocs({ uid, site, siteId, version, versionId, slug, title, tasks, siteHost, stamp }) {
  const files = version.files || {};
  const homePage = version.homePage || site.homePage || 'index.html';
  const serveMode = version.serveMode === 'spa' ? 'spa' : 'multipage';
  const versionDoc = {
    createdAt: stamp,
    files,
    fileCount: version.fileCount || Object.keys(files).length,
    totalBytes: version.totalBytes || 0,
    changeSummary: { added: [], changed: [], removed: [] },
    homePage,
    serveMode,
    source: 'web',
    deployMethod: 'copy',
    copiedFrom: { siteId, versionId },
  };
  if (version.sizes) versionDoc.sizes = version.sizes;

  const urlDoc = {
    userId: uid,
    brand: 'aero',
    generatedUrl: slug,
    title,
    type: 'html',
    homePage,
    serveMode,
    deployMethod: 'copy',
    sourceSiteId: siteId,
    sourceVersionId: versionId,
    sourceSiteTitle: site.title || site.generatedUrl || '',
    sourceSiteHost: siteHost || (site.generatedUrl ? `${site.generatedUrl}.aeroapp.ai` : ''),
    createdAt: stamp,
    updatedAt: stamp,
  };
  // Keep a password-gated site gated at the test's own address too (testers
  // still get in through the signed test cookie, as on any site test).
  if (site.faviconUrl) urlDoc.faviconUrl = site.faviconUrl;
  if (site.isPasswordActive !== undefined) urlDoc.isPasswordActive = site.isPasswordActive;
  if (site.encryptedPassword !== undefined) urlDoc.encryptedPassword = site.encryptedPassword;
  return { versionDoc, urlDoc };
}

async function slugIsFree(db, slug) {
  const snap = await db.collectionGroup('url').where('generatedUrl', '==', slug).limit(1).get();
  return snap.empty;
}

/** Copies of one site, newest first: [{ id, ...doc }]. */
export async function listTestCopies(uid, siteId) {
  if (!uid || !siteId) return [];
  const snap = await firebase.firestore()
    .collection('user').doc(uid).collection('url')
    .where('sourceSiteId', '==', siteId)
    .get();
  const ms = (v) => (v?.toMillis ? v.toMillis() : v ? new Date(v).getTime() : 0);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
}

/**
 * Make a test copy of a live HTML site. Returns { urlId, landing }.
 * Writes the version and the url doc in one batch.
 */
export async function createTestCopy({ uid, siteId, name, tasks, siteHost }) {
  if (!uid) throw new Error('Not signed in');
  if (!siteId) throw new Error('Missing site');
  const db = firebase.firestore();
  const urlRef = (id) => db.collection('user').doc(uid).collection('url').doc(id);

  const siteSnap = await urlRef(siteId).get();
  if (!siteSnap.exists) throw new Error('Site not found');
  const site = siteSnap.data() || {};
  if (site.type !== 'html' || !site.activeVersion) {
    throw new Error('This site has no live version yet');
  }
  if (isTestCopy(site)) throw new Error('This is already a test');
  const versionId = site.activeVersion;
  const versionSnap = await urlRef(siteId).collection('versions').doc(versionId).get();
  if (!versionSnap.exists) throw new Error('The live version could not be found');
  const version = versionSnap.data() || {};

  const existing = await listTestCopies(uid, siteId);
  const n = existing.length + 1;

  let slug = null;
  for (const candidate of testSlugCandidates(site.generatedUrl || siteId, n)) {
    // eslint-disable-next-line no-await-in-loop
    if (await slugIsFree(db, candidate)) { slug = candidate; break; }
  }
  if (!slug) slug = await generateReadableSlug(db);

  const stamp = firebase.firestore.FieldValue.serverTimestamp();
  const { versionDoc, urlDoc } = buildTestCopyDocs({
    uid, site, siteId, version, versionId, slug, siteHost, stamp,
    title: testCopyTitle(site.title || site.generatedUrl, n, name),
  });
  const newVersionRef = urlRef(slug).collection('versions').doc();
  urlDoc.activeVersion = newVersionRef.id;
  urlDoc.userTesting = {
    enabled: true,
    status: 'active',
    prototypeType: 'site',
    pinnedVersionId: newVersionRef.id,
    ...UT_DEFAULTS,
    tasks: taskDocs(tasks),
  };

  const batch = db.batch();
  batch.set(newVersionRef, versionDoc);
  batch.set(urlRef(slug), urlDoc);
  await batch.commit();

  return { urlId: slug, landing: `/user-testing/${slug}/setup` };
}

const asDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

function testStatusKind(ut) {
  const st = ut && ut.status;
  if (st === 'active') return 'open';
  if (st === 'completed') return 'closed';
  return 'draft';
}

/**
 * Rows for a site's "Tests of this site": its copies (newest first), then the
 * older test kept on the site's own doc, if it has one. Pure.
 *   { id, title, link, statusKind, madeLabel, own }
 */
export function siteTestRows(site, copies) {
  const row = (d, own) => {
    const made = asDate(d.createdAt);
    const madeOk = made && !Number.isNaN(made.getTime());
    return {
      id: d.id,
      title: d.title || d.generatedUrl || 'Untitled test',
      link: d.generatedUrl ? `aeroapp.ai/${d.generatedUrl}?test=1` : '',
      statusKind: testStatusKind(d.userTesting),
      madeLabel: madeOk ? `Made ${made.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : '',
      own,
    };
  };
  const rows = (copies || []).filter((d) => d.userTesting).map((d) => row(d, false));
  if (site && site.userTesting && site.userTesting.enabled === true) rows.push(row(site, true));
  return rows;
}
